import React, { Component } from 'react';
import NoSsr from '@material-ui/core/NoSsr';
import { Clear } from '@material-ui/icons';
import PropTypes from 'prop-types';
import TrailFormSuccess from '../../shared/TrailFormSuccess';
import Modal from '../../shared/Modal';
import styles from './styles/styles.module.scss';
import StarRating from '../../shared/StarRating';
import DatePicker from '../../shared/DatePicker';
import FormButton from '../FormButton';
import SelectDropdown from '../SelectDropdown';
import CloudCheckbox from '../CloudCheckbox';
import { activities, obstacles, defaultHikingActivity } from '../../../utils/activity_util';
import { monthDayYear } from '../../../utils/time_helpers';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';

class ReviewFormModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      comment: props.comment_source || props.comment,
      rating: props.rating,
      successfulSubmission: false,
      serverError: '',
      submitting: false,
      activeStep: ReviewFormModal.FIRST_STEP, // total of two steps
      selectedActivity: activities.find(activity => activity.value === props.activity.uid) || defaultHikingActivity(),
      date: props.date,
      activeObstacles: props.obstacles.map(obs => obs.uid)
    };
  }

  static FIRST_STEP = 1;
  static LAST_STEP = 2;

  addReview(trailReview) {
    const payload = { detail: trailReview };
    if (this.props.exploreFlag) {
      const event = new CustomEvent('addReview', payload);
      window.document.dispatchEvent(event);
      return;
    }
    this.props.onReviewAdd(payload);
  }

  updateReview(trailReview) {
    const payload = { detail: trailReview };
    if (this.props.exploreFlag) {
      const event = new CustomEvent('updateReview', payload);
      window.document.dispatchEvent(event);
      return;
    }

    this.props.updateReview(payload);
  }

  updateComment = e => {
    const { value } = e.currentTarget;

    this.setState({ comment: value });
  };

  setRating = (e, rating) => {
    e.preventDefault();
    this.setState({ rating });
  };

  addObstacle(uid) {
    this.setState({ activeObstacles: [...this.state.activeObstacles, uid] });
  }

  removeObstacle(uid) {
    const activeObstacles = this.state.activeObstacles.filter(obstacle => {
      return obstacle !== uid;
    });

    this.setState({ activeObstacles });
  }

  // gets
  getFormParams() {
    const { comment, rating, selectedActivity, date, activeObstacles } = this.state;

    return {
      comment,
      rating,
      activity_date: date,
      activity: selectedActivity.value,
      obstacle_uids: activeObstacles
    };
  }

  getFormButtonText() {
    if (!this.props.multistep) {
      return StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.POST_REVIEW;
    }

    if (this.state.activeStep === ReviewFormModal.FIRST_STEP) {
      return StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.NEXT;
    }

    return StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.POST;
  }

  // handlers
  handleSuccess = response => {
    const trailReview = response.trail_reviews[0];

    this.props.id ? this.updateReview(trailReview) : this.addReview(trailReview);
    this.setState({ successfulSubmission: true, submitting: false, serverError: '' });
  };

  handleError = err => {
    this.setState({ serverError: StringConstantsMixin.strings.PROGRESS_MESSAGES.ERROR_SAVING, submitting: false });
  };

  handleSelectChange = e => {
    const value = this.props.isMobile ? e.currentTarget.value : e.currentTarget.dataset.value;

    const selectedActivity = activities.find(activity => activity.value === value);
    this.setState({ selectedActivity });
  };

  handleDateChange = date => {
    this.setState({ date });
  };

  submitOrNextStep = e => {
    e.preventDefault();

    if (this.props.multistep && this.state.activeStep !== ReviewFormModal.LAST_STEP) {
      return this.setState({ activeStep: ReviewFormModal.LAST_STEP });
    }

    return this.handleSubmit();
  };

  handleCloudCheckboxChange = e => {
    const { value } = e.currentTarget;
    return this.state.activeObstacles.includes(value) ? this.removeObstacle(value) : this.addObstacle(value);
  };

  handleSubmit() {
    let url;
    this.setState({ submitting: true });
    const params = this.getFormParams();
    // if Trail Review record exists create
    if (this.props.id) {
      url = `/api/alltrails/users/${this.props.user.id}/reviews/${this.props.id}?key=${ServerCommunicationUtil.apiKey}`;
      ServerCommunicationUtil.putApiEndpoint(url, params, this.handleSuccess, this.handleError, () => {});
    } else {
      url = `/api/alltrails/trails/${this.props.trailId}/reviews?key=${ServerCommunicationUtil.apiKey}`;
      ServerCommunicationUtil.postApiEndpoint(url, params, this.handleSuccess, this.handleError, () => {});
    }
  }

  isActiveObstacle(obstacle) {
    return this.state.activeObstacles.includes(obstacle.uid);
  }

  backToFirstStep = () => {
    this.setState({ activeStep: ReviewFormModal.FIRST_STEP });
  };

  renderCloudCheckboxes() {
    return obstacles.map(obstacle => {
      return (
        <CloudCheckbox
          key={obstacle.uid}
          label={obstacle.label}
          value={obstacle.uid}
          onChange={this.handleCloudCheckboxChange}
          checked={this.isActiveObstacle(obstacle)}
        />
      );
    });
  }

  renderFirstFormStep() {
    return (
      <>
        <div className={styles.ratingAndTextArea}>
          <div className={styles.starRatingContainer}>
            <StarRating config={this.state.rating} handler={this.setRating} iconClassName={styles.starRatingIconClass} />
          </div>
          <textarea
            placeholder={StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.GIVE_BACK_TO_THE_COMMUNITY}
            className={styles.commentTextArea}
            value={this.state.comment}
            onChange={this.updateComment}
            name="comment"
          />
        </div>
      </>
    );
  }

  renderSecondFormStep() {
    return (
      <>
        <div className={styles.activityAndDate}>
          <div className={styles.sectionLabel}>{StringConstantsMixin.strings.ACTIVITY}*</div>
          <div className={styles.dropdowns}>
            <SelectDropdown
              options={activities}
              handleChange={this.handleSelectChange}
              defaultItem={this.state.selectedActivity}
              isMobile={this.props.isMobile}
            />
            <div className={styles.spacer} />
            <DatePicker date={this.state.date} onChange={this.handleDateChange} locale={this.props.locale} abbreviateMonth />
          </div>
        </div>
        <div className={styles.trailConditions}>
          <div className={`${styles.sectionLabel} ${styles.trailConditionLabel}`}>{StringConstantsMixin.strings.TRAIL_CONDITIONS}*</div>
          <div className={styles.cloudCheckboxesContainer}>{this.renderCloudCheckboxes()}</div>
        </div>
      </>
    );
  }

  renderActiveFormStep() {
    switch (this.state.activeStep) {
      case 1:
        return this.renderFirstFormStep();
      case 2:
        return this.renderSecondFormStep();
      default:
        return null;
    }
  }

  renderActiveFormContent() {
    if (!this.props.multistep) {
      return this.renderFirstFormStep();
    }

    return this.renderActiveFormStep();
  }

  renderForm() {
    return (
      <NoSsr>
        <form onSubmit={this.submitOrNextStep} className={styles.form}>
          {this.renderActiveFormContent()}
          {this.state.serverError.length > 0 && <div className={styles.serverError}>{this.state.serverError}</div>}
          <div className={styles.formButtonContainer}>
            {this.state.activeStep === ReviewFormModal.LAST_STEP && (
              <button type="button" className={styles.cancelButton} onClick={this.backToFirstStep}>
                {StringConstantsMixin.strings.BACK}
              </button>
            )}
            <FormButton
              value={this.getFormButtonText()}
              disabled={this.state.rating === null || this.state.submitting}
              className={styles.formButton}
            />
          </div>
        </form>
      </NoSsr>
    );
  }

  renderStepsOrActivityAndDate() {
    const subText = this.props.multistep
      ? StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.stepText(this.state.activeStep, ReviewFormModal.LAST_STEP)
      : `${monthDayYear(this.props.date, this.props.locale)} ${'\u2022'} ${this.props.activity.name}`;

    return <div className={styles.dateAndActivityName}>{subText}</div>;
  }

  renderTitle() {
    return (
      <div className={styles.trailDataAndClear}>
        <div className={styles.trailName}>{this.props.trailName}</div>
        <div className={styles.dateAndActivityName}>{this.renderStepsOrActivityAndDate()}</div>
      </div>
    );
  }

  renderModalContent() {
    if (this.state.successfulSubmission) {
      return <TrailFormSuccess variant="review" />;
    }

    return (
      <>
        {this.renderTitle()}
        {this.renderForm()}
      </>
    );
  }

  render() {
    return (
      <Modal handleBackgroundClick={this.props.closeReviewModal}>
        <div className={styles.reviewFormContainer}>
          <div className={styles.clearContainer}>
            <button type="button" className={styles.clearIcon} onClick={this.props.closeReviewModal}>
              <Clear />
            </button>
          </div>
          {this.renderModalContent()}
        </div>
      </Modal>
    );
  }
}

ReviewFormModal.propTypes = {
  id: PropTypes.number,
  trailId: PropTypes.number.isRequired,
  trailName: PropTypes.string,
  user: PropTypes.shape({
    id: PropTypes.number
  }).isRequired,
  closeReviewModal: PropTypes.func.isRequired,
  date: PropTypes.string,
  comment: PropTypes.string,
  rating: PropTypes.number,
  obstacles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      uid: PropTypes.string
    })
  ),
  activity: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    uid: PropTypes.string
  }),
  multistep: PropTypes.bool,
  exploreFlag: PropTypes.bool
};

ReviewFormModal.defaultProps = {
  id: null,
  date: new Date(),
  rating: 1,
  comment: '',
  trailName: '',
  activity: {
    value: 'hiking'
  },
  obstacles: [],
  multistep: false,
  exploreFlag: false,
  isMobile: true
};

export default ReviewFormModal;
